/**
 * UserDetailModal Component
 * 사용자 상세 정보 모달 (생성/수정)
 */
import React, { useState, useEffect, useCallback } from 'react';
import { CreateUserRequestDto, UpdateUserRequestDto } from '../types/user.types';
import { ToastType } from "@/shared/components/feedback/Toast/Toast.types";
import { useToast } from "@/shared/components/feedback/Toast/ToastContext";
import UserFormSection from './UserFormSection';
import UserInfoSection from './UserInfoSection';
import {
    UserDetailModalProps,
    FormDataType,
    TOAST_MESSAGES,
    MODAL_TITLES,
    BUTTON_LABELS,
    getCreateModeInitialData,
    getEditModeInitialData,
    validateCreateForm,
} from './userDetailModal.helpers';
import '../styles/UserDetailModal.css';

// ======== 컴포넌트 ========

const UserDetailModal: React.FC<UserDetailModalProps> = ({
                                                             user,
                                                             prmCodes,
                                                             mode = 'edit',
                                                             onClose,
                                                             onUpdate,
                                                             onCreate,
                                                         }) => {
    const { showToast } = useToast();
    const isCreateMode = mode === 'create';

    // ======== 상태 ========

    const [formData, setFormData] = useState<FormDataType>(getCreateModeInitialData());
    const [isSubmitting, setIsSubmitting] = useState(false);

    // ======== 폼 초기화 ========

    /**
     * 모드 변경 시 폼 데이터 초기화
     */
    useEffect(() => {
        if (isCreateMode) {
            setFormData(getCreateModeInitialData());
        } else if (user) {
            setFormData(getEditModeInitialData(user));
        }
    }, [user, isCreateMode]);

    // ======== 핸들러 ========

    /**
     * 필드 변경 핸들러
     */
    const handleChange = useCallback((
        field: keyof (CreateUserRequestDto | UpdateUserRequestDto),
        value: string
    ) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    }, []);

    /**
     * 폼 제출 핸들러
     */
    const handleSubmit = useCallback(async (e: React.FormEvent) => {
        e.preventDefault();

        // 생성 모드 유효성 검사
        if (isCreateMode && !validateCreateForm(formData as CreateUserRequestDto)) {
            showToast(TOAST_MESSAGES.REQUIRED_FIELDS, { type: ToastType.ERROR });
            return;
        }

        setIsSubmitting(true);
        try {
            if (isCreateMode && onCreate) {
                await onCreate(formData as CreateUserRequestDto);
            } else if (!isCreateMode && user && onUpdate) {
                await onUpdate(user.userId, formData as UpdateUserRequestDto);
            }
            onClose();
        } catch {
            // 에러는 상위에서 처리
        } finally {
            setIsSubmitting(false);
        }
    }, [isCreateMode, formData, onCreate, user, onUpdate, onClose, showToast]);

    /**
     * 모달 백드롭 클릭 핸들러
     */
    const handleBackdropClick = useCallback((e: React.MouseEvent) => {
        if (e.target === e.currentTarget) onClose();
    }, [onClose]);

    // ======== 조기 반환 ========

    if (!isCreateMode && !user) return null;

    // ======== 렌더링 ========

    return (
        <div className="modal" onClick={handleBackdropClick}>
            <div className="modal-content user-detail-modal">
                {/* 헤더 */}
                <div className="modal-header">
                    <h2 className="modal-title">
                        {isCreateMode ? MODAL_TITLES.CREATE : MODAL_TITLES.EDIT}
                    </h2>
                    <button className="modal-close" onClick={onClose}>×</button>
                </div>

                {/* 본문 */}
                <div className="modal-body">
                    <form onSubmit={handleSubmit}>
                        {/* 폼 입력 섹션 */}
                        <UserFormSection
                            formData={formData}
                            user={user}
                            prmCodes={prmCodes}
                            isCreateMode={isCreateMode}
                            onChange={handleChange}
                        />

                        {/* 등록 정보 섹션 (수정 모드 전용) */}
                        {!isCreateMode && user && <UserInfoSection user={user} />}

                        {/* 푸터 버튼 */}
                        <div className="modal-footer">
                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={onClose}
                            >
                                {BUTTON_LABELS.CANCEL}
                            </button>
                            <button
                                type="submit"
                                className="btn btn-primary"
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? BUTTON_LABELS.SAVING : BUTTON_LABELS.SAVE}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default UserDetailModal;
